import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import type { 
  ServiceResponse, 
  ServiceOptionResponse, 
  CreateServiceRequest, 
  UpdateServiceRequest,
  CreateServiceOptionRequest,
  UpdateServiceOptionRequest 
} from '@/types/services';

interface UseServicesParams {
  search?: string;
  vendorId?: string;
  serviceTypeId?: string;
  serviceSubCategoryId?: string;
  organizationId?: string;
}

// Services hooks
export function useServices() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['services'],
    queryFn: async () => {
      setLoading(true);
      setError(null);
      try {
        const url = '/marketplace/services';
        const response = await apiRequest<ServiceResponse[]>("GET", url);
        return response;
      } catch (err: any) {
        const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch services';
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    }
  });

  return {
    data: query.data,
    loading: loading || query.isLoading,
    error: error || (query.error ? String(query.error) : null),
    refetch: query.refetch
  };
}

export function useService(id: string) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['service', id],
    queryFn: async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiRequest("GET", `/marketplace/services/${id}`);
        return response.data || response;
      } catch (err: any) {
        const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch service';
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    enabled: !!id
  });

  return {
    data: query.data,
    loading: loading || query.isLoading,
    error: error || (query.error ? String(query.error) : null),
    refetch: query.refetch
  };
}

// Service Options hooks
export function useServiceOptions(serviceId: string) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['serviceOptions', serviceId],
    queryFn: async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiRequest("GET", `/marketplace/services/${serviceId}/options`);
        // apiRequest already unwraps the .data field, so response should be the array directly
        return Array.isArray(response) ? response : [];
      } catch (err: any) {
        const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch service options';
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    enabled: !!serviceId
  });

  return {
    data: query.data,
    loading: loading || query.isLoading,
    error: error || (query.error ? String(query.error) : null),
    refetch: query.refetch
  };
}

export function useServiceOption(serviceId: string, optionId: string) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['serviceOption', serviceId, optionId],
    queryFn: async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiRequest("GET", `/marketplace/services/${serviceId}/options/${optionId}`);
        return response.data || response;
      } catch (err: any) {
        const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch service option';
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    enabled: !!serviceId && !!optionId
  });

  return {
    data: query.data,
    loading: loading || query.isLoading,
    error: error || (query.error ? String(query.error) : null),
    refetch: query.refetch
  };
}

// Mutations
export function useCreateService() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: CreateServiceRequest) => {
      const response = await apiRequest("POST", "/marketplace/services", data);
      return response.data as ServiceResponse;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] });
    }
  });
}

export function useUpdateService() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateServiceRequest }) => {
      const response = await apiRequest("PUT", `/marketplace/services/${id}`, data);
      return response.data as ServiceResponse;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['services'] });
      queryClient.invalidateQueries({ queryKey: ['service', data.id] });
    }
  });
}

export function useDeleteService() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/marketplace/services/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] });
    }
  });
}

export function useCreateServiceOption() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ serviceId, data }: { serviceId: string; data: CreateServiceOptionRequest }) => {
      const response = await apiRequest("POST", `/marketplace/services/${serviceId}/options`, data);
      return response.data as ServiceOptionResponse;
    },
    onSuccess: (data, variables) => {
      // Invalidate the specific service options query
      queryClient.invalidateQueries({ queryKey: ['serviceOptions', variables.serviceId] });
      // Also invalidate the general services query to ensure consistency
      queryClient.invalidateQueries({ queryKey: ['services'] });
      // Force refetch of the specific service options
      queryClient.refetchQueries({ queryKey: ['serviceOptions', variables.serviceId] });
    }
  });
}

export function useUpdateServiceOption() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ serviceId, optionId, data }: { serviceId: string; optionId: string; data: UpdateServiceOptionRequest }) => {
      const response = await apiRequest("PUT", `/marketplace/services/${serviceId}/options/${optionId}`, data);
      return response.data as ServiceOptionResponse;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['serviceOptions', variables.serviceId] });
      queryClient.invalidateQueries({ queryKey: ['serviceOption', variables.serviceId, variables.optionId] });
    }
  });
}

export function useDeleteServiceOption() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ serviceId, optionId }: { serviceId: string; optionId: string }) => {
      await apiRequest("DELETE", `/marketplace/services/${serviceId}/options/${optionId}`);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['serviceOptions', variables.serviceId] });
    }
  });
}